import { MousePointer, Hand, PenTool, Crosshair, Move, Trash2, Send, Sparkles, Magnet } from 'lucide-react';

const TOOLS = [
  { key: 'select', icon: MousePointer, label: 'Select', shortcut: 'V' },
  { key: 'pan', icon: Hand, label: 'Pan', shortcut: 'H' },
  { key: 'draw', icon: PenTool, label: 'Draw', shortcut: 'D' },
  { key: 'seed', icon: Crosshair, label: 'Auto-Detect', shortcut: 'S' },
  { key: 'edit', icon: Move, label: 'Edit Vertices', shortcut: 'E' },
  { key: 'delete', icon: Trash2, label: 'Delete', shortcut: 'X' },
];

export default function EditorToolbar({ mode, onModeChange, onPublish, onAutoLabel, onCancelDraw, isDrawing, published, labeling, labelProgress, snapEnabled, onSnapToggle, confidenceThreshold, onConfidenceChange, onBulkAccept, pendingBelowCount, pendingAboveCount }) {
  const progressText = labeling && labelProgress
    ? `${labelProgress.labeled}/${labelProgress.total}`
    : null;

  return (
    <div className="h-9 bg-card/90 border-b border-border/50 flex items-center justify-between px-3" data-testid="editor-toolbar">
      <div className="flex items-center gap-0.5">
        <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mr-2">Tools</span>
        {TOOLS.map((t) => {
          const Icon = t.icon;
          const active = mode === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onModeChange(t.key)}
              title={`${t.label} (${t.shortcut})`}
              data-testid={`tool-${t.key}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                active
                  ? 'bg-orange-600/20 text-primary border border-primary/40'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
        {isDrawing && (
          <button
            onClick={onCancelDraw}
            data-testid="cancel-draw-btn"
            className="ml-2 px-2.5 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider text-rose-400 border border-rose-800/50 hover:bg-rose-950/30 transition-colors"
          >
            Cancel (Esc)
          </button>
        )}
        <div className="w-px h-5 bg-secondary mx-2" />
        {/* Snap toggle */}
        <button
          onClick={onSnapToggle}
          data-testid="snap-toggle-btn"
          title="Toggle Snap-to-Wall (G)"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
            snapEnabled
              ? 'bg-emerald-950/20 text-emerald-400 border border-emerald-800/40'
              : 'text-muted-foreground hover:text-foreground border border-border/50 hover:border-slate-600'
          }`}
        >
          <Magnet className="w-3.5 h-3.5" />
          Snap
        </button>
        <button
          onClick={onAutoLabel}
          disabled={labeling}
          data-testid="auto-label-btn"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
            labeling
              ? 'bg-violet-950/20 text-violet-400 border border-violet-800/40 cursor-wait'
              : 'text-violet-400 hover:bg-violet-950/30 border border-violet-800/40 hover:border-violet-600/50'
          }`}
        >
          <Sparkles className={`w-3.5 h-3.5 ${labeling ? 'animate-spin' : ''}`} />
          {labeling ? (
            <span className="flex items-center gap-1.5">
              Labeling
              {progressText && (
                <span className="font-mono text-[9px] bg-violet-900/40 px-1.5 py-0.5 rounded" data-testid="label-progress-badge">
                  {progressText}
                </span>
              )}
            </span>
          ) : 'AI Label'}
        </button>

        {/* Confidence threshold slider */}
        {confidenceThreshold !== undefined && (
          <>
            <div className="w-px h-5 bg-secondary mx-2" />
            <div className="flex items-center gap-2" data-testid="confidence-slider-group">
              <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground whitespace-nowrap">Conf</span>
              <input
                type="range"
                min="0" max="100" step="5"
                value={Math.round(confidenceThreshold * 100)}
                onChange={(e) => onConfidenceChange?.(parseInt(e.target.value) / 100)}
                className="w-20 h-1 accent-amber-500 bg-secondary rounded cursor-pointer"
                data-testid="confidence-slider"
              />
              <span className="font-mono text-[9px] text-amber-400 w-8" data-testid="confidence-value">
                {Math.round(confidenceThreshold * 100)}%
              </span>
              {pendingAboveCount > 0 && (
                <button
                  onClick={onBulkAccept}
                  data-testid="bulk-accept-btn"
                  className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] font-heading uppercase tracking-wider text-emerald-400 border border-emerald-800/40 hover:bg-emerald-950/30 transition-all"
                >
                  Accept {pendingAboveCount}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <button
        onClick={onPublish}
        disabled={published}
        data-testid="publish-btn"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
          published
            ? 'bg-emerald-950/20 text-emerald-600 border border-emerald-900/30 cursor-default'
            : 'bg-emerald-600/15 text-emerald-400 border border-emerald-700/50 hover:bg-emerald-600/25'
        }`}
      >
        <Send className="w-3.5 h-3.5" />
        {published ? 'Published' : 'Save & Publish'}
      </button>
    </div>
  );
}
